import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import { ReferentialKey } from "api/referential";
import { useAppSelector } from "app/hooks";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { legalFormCodeFromNatureJuridique } from "helpers/legalFormCodeFromNatureJuridique";
import { referentialsApi } from "services/swagger-api";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { BaseLegalEntityFormFieldNames } from "shared/enums/baseLegalEntityFormFieldNames";
import { LegalEntityFormFieldNames } from "../enums/legalEntityFormFieldNames";

const SIRET_LENGTH = 14;
const SIRET_LOOKUP_DEBOUNCE_MS = 600;

/**
 * Watcher sans rendu du formulaire personne morale : SIRET complet saisi -> raison
 * sociale et adresse du siège via le proxy annuaire (toast « syndic reconnu » si le
 * SIRET gère des copropriétés au registre RNIC). Le sens nom -> SIRET passe, lui, par
 * la sélection explicite dans l'autocomplete du champ nom (FormikCompanySearch).
 * Ne remplit que les champs vides — la saisie de l'utilisateur prime toujours.
 * La forme juridique n'est remplie que si la catégorie juridique de l'annuaire
 * correspond à un code connu du référentiel (jamais avec la raison sociale).
 */
export const SyndicSiretPrefill = () => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const lastLookedUpSiret = useRef<string | null>(null);
  const referentialsData = useAppSelector(selectReferentialData);
  const legalFormCodes = useMemo(
    () => (referentialsData[ReferentialKey.LegalForm] ?? []).map((item) => item.code),
    [referentialsData]
  );

  const rawSiret = values[BaseLegalEntityFormFieldNames.RegistrationNumber];
  const siret = typeof rawSiret === "string" ? rawSiret.replace(/\s/g, "") : "";

  /** Remplit un champ seulement s'il est vide ; renvoie true si rempli. */
  const fillIfEmpty = useCallback(
    (field: string, value?: string | null) => {
      if (!value) return false;
      const current = values[field];
      if (typeof current === "string" && current.trim() !== "") return false;
      setFieldValue(field, value);
      return true;
    },
    [values, setFieldValue]
  );

  // SIRET -> identité officielle (raison sociale, adresse du siège) + reconnaissance RNIC.
  useEffect(() => {
    if (siret.length !== SIRET_LENGTH || siret === lastLookedUpSiret.current) {
      return;
    }

    const timeout = setTimeout(async () => {
      lastLookedUpSiret.current = siret;

      let prefilled = false;
      let raisonSociale: string | null = null;
      try {
        const entreprise = await referentialsApi.getEnterpriseBySiret({ siret });
        raisonSociale = entreprise?.raisonSociale ?? null;

        prefilled = [
          fillIfEmpty(LegalEntityFormFieldNames.CompanyName, entreprise?.raisonSociale),
          fillIfEmpty(
            BaseLegalEntityFormFieldNames.LegalReason,
            legalFormCodeFromNatureJuridique(entreprise?.natureJuridiqueCode, legalFormCodes)
          ),
          fillIfEmpty(AddressFormFieldNames.Address, entreprise?.adresseLigne1),
          fillIfEmpty(AddressFormFieldNames.PostalCode, entreprise?.codePostal),
          fillIfEmpty(AddressFormFieldNames.City, entreprise?.commune),
          // L'annuaire ne couvre que la France ; même code court que la sélection Google.
          fillIfEmpty(AddressFormFieldNames.Country, entreprise?.adresseLigne1 ? "FR" : null),
        ].some(Boolean);
      } catch {
        // Annuaire indisponible ou SIRET inconnu : on tente encore le RNIC ci-dessous.
      }

      // Reconnaissance syndic au RNIC : complète le nom si l'annuaire n'a rien donné, et
      // son toast remplace le toast générique (l'utilisateur sait que le portefeuille suivra).
      try {
        const entries = await referentialsApi.getCoproRegistryBySiret({ siret, maxResultCount: 1 });
        const representantLegal = entries[0]?.representantLegal;
        if (representantLegal) {
          fillIfEmpty(LegalEntityFormFieldNames.CompanyName, representantLegal);
          toast.info(`Syndic reconnu au registre des copropriétés : ${representantLegal}`);
          return;
        }
      } catch {
        // Registre indisponible : la saisie manuelle continue sans bruit.
      }

      if (prefilled && raisonSociale) {
        toast.info(`Entreprise reconnue : ${raisonSociale}`);
      }
    }, SIRET_LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [siret, fillIfEmpty, legalFormCodes]);

  return null;
};
